import { Component, OnInit, Input, OnChanges, SimpleChanges, forwardRef, Output, EventEmitter } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR, Validator, AbstractControl, NG_VALIDATORS } from "@angular/forms";
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

import { Observable, Subject, of, first } from "rxjs";
import { debounceTime, distinctUntilChanged, startWith, map, switchMap } from "rxjs/operators";

import { FacilitySelectorService, FacilitySearchParms } from "../../services/facility-selector.service";
import { FacilitySelectorSearchDialog } from "./facility-selector-search.dialog";

import * as _ from "lodash-es";
import * as Facility from "projects/velocity/src/app/shared/models/facility";

export class LocationWithChildren {
  public name: string;
  public locations: SelectableFacility[];
}

export class SelectableFacility {
  public locationName: string;
  public locationNumber: string;
}

@Component({
  selector: 'velocity-facility-selector',
  templateUrl: './facility-selector.component.html',
  styleUrls: ['./facility-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FacilitySelectorComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => FacilitySelectorComponent),
      multi: true,
    }
  ]
})
export class FacilitySelectorComponent implements OnInit, OnChanges, ControlValueAccessor, Validator {

  private validationChanged: () => void;
  public filteredFacilities: Observable<LocationWithChildren[]>;
  public selectedLocation: Observable<SelectableFacility>;
  public searchText: string;
  public loading = false;
  private onChange: Subject<string>;
  public isDisabled = false;
  private updateValue: (_: any) => void;
  private touchValue: () => void;
  public errorMessage: string;
  private isInitialized: boolean = false;
  private searchDisabled: boolean = true;

  @Input()
  public locationLevel: string;
  @Input()
  public selectorDataSource: string;
  @Input()
  public addString: string;
  @Input()
  public placeholder: string;
  @Input()
  public autocompletePanelWidth: string;

  @Output()
  public selectedFacility = new EventEmitter<SelectableFacility>();

  @Output()
  public initialized = new EventEmitter<boolean>();

  @Output()
  public onBlurEvent = new EventEmitter<any>();

  constructor(
    private readonly facilitySelectorService: FacilitySelectorService,
    private readonly dialog: MatDialog
  ) { }

  public onBlur() {
    if (this.touchValue)
      this.touchValue();

    this.onBlurEvent.emit(null);
  }

  public ngOnChanges(changes: SimpleChanges) {
    let needToInitialize = false;
    if (changes.selectorDataSource && changes.selectorDataSource.currentValue !== changes.selectorDataSource.previousValue) {
      needToInitialize = true;
    }

    if (changes.locationLevel && changes.locationLevel.currentValue !== changes.locationLevel.previousValue) {
      needToInitialize = true;
    }

    if (needToInitialize)
      this.initialize(this.searchText);
  }

  public doChange(newText: string) {
    this.searchText = newText;
    if (this.onChange)
      this.onChange.next(newText);
  }

  private Initialize() {
    if (!this.isInitialized) {
      this.initialized.emit(true);
      this.isInitialized = true;
    }
  }

  public initialize(initialValue: string) {
    if (this.locationLevel && this.locationLevel.toLocaleLowerCase() === "facility") {
      this.loading = true;

      this.onChange = new Subject<string>();

      this.filteredFacilities = this.onChange
        .pipe(
          startWith(initialValue),
          debounceTime(300),
          distinctUntilChanged(),
          switchMap(
            (searchText) => {
              this.loading = true;

              const searchParms = new FacilitySearchParms();
              searchParms.Page = 1;
              searchParms.PageSize = 50;
              searchParms.FacilitySearch = searchText;
              searchParms.DistrictSearch = searchText;
              searchParms.AreaSearch = searchText;
              searchParms.RegionSearch = searchText;
              const result = this.facilitySelectorService.getFacilities(this.selectorDataSource, 'facility', searchParms);

              return result;
            }
          ),
          map(facilities => {
            const regions: string[] = _.orderBy(_.uniq(_.map(facilities, (facility) => { return facility.regionNumber })));
            const areas: string[] = _.orderBy(_.uniq(_.map(facilities, (facility) => { return facility.areaNumber })));
            const districts: string[] = _.orderBy(_.uniq(_.map(facilities, (facility) => { return facility.districtNumber })));

            this.searchDisabled = !!_.find(facilities, function (n) {
              return !!n.isCustomer;
            });

            const groupedLocations: LocationWithChildren[] = [];

            _.forEach(regions,
              (region) => {
                //var regionName = _.find(facilities, (facility) => { return facility.regionNumber === region; }).regionName;
                _.forEach(areas,
                  (area) => {
                    //var areaName = _.find(facilities, (facility) => { return facility.areaNumber === area; }).areaName;

                    _.forEach(districts,
                      (district) => {
                        //                var districtName = _.find(facilities, (facility) => { return facility.districtNumber === district; }).districtName;

                        const districtFacilities: Facility.Facility[] = _.filter(facilities,
                          (facility) => {
                            return facility.regionNumber === region &&
                              facility.areaNumber === area &&
                              facility.districtNumber === district;
                          });

                        if (districtFacilities.length > 0) {
                          groupedLocations.push({
                            name: `${(region != null ? region + '/' : '')}${(area != null ? area + '/' : '')}${(district != null ? district : '')}`,
                            locations: _.orderBy(_.map(_.filter(facilities,
                              (facility) => {
                                return facility.regionNumber === region &&
                                  facility.areaNumber === area &&
                                  facility.districtNumber === district;
                              }),
                              (facility) => {
                                return {
                                  locationNumber: facility.facilityNumber,
                                  locationName: facility.facilityName
                                } as SelectableFacility;
                              }),
                              (location) => {
                                return location.locationName;
                              })
                          } as LocationWithChildren);
                        }
                      });
                  });
              });

            this.loading = false;
            this.Initialize();

            return groupedLocations;
          })
        );

      this.selectedLocation = this.onChange
        .pipe(
          startWith(initialValue),
          debounceTime(100),
          distinctUntilChanged(),
          switchMap(
            (searchText) => {
              let result: Observable<Facility.Facility[]>;
              if (searchText) {
                const searchParms = new FacilitySearchParms();
                searchParms.FacilityId = searchText;
                result = this.facilitySelectorService.getFacilities(this.selectorDataSource, 'facility', searchParms);
              } else {
                result = of([] as Facility.Facility[]);
              }

              return result.pipe(
                map(facilities => {
                  return _.find(_.uniqWith(_.map(facilities,
                    (facility) => {
                      return {
                        locationNumber: facility.facilityNumber,
                        locationName: facility.facilityName
                      } as SelectableFacility;
                    }), (arrVal, othVal) => {
                      return arrVal.locationNumber === othVal.locationNumber;
                    }),
                    (facility: SelectableFacility) => {
                      return facility.locationNumber === searchText;
                    }) as SelectableFacility;
                })
              );
            }
          )
        );

      this.selectedLocation
        .subscribe(result => {
          if (this.updateValue) {
            if (result)
              this.updateValue(result.locationNumber);
            else
              this.updateValue(null);
          }

          this.selectedFacility.emit(result);

          if (this.validationChanged)
            this.validationChanged();
        });

      this.onChange
        .subscribe((): void => {
          if (this.validationChanged)
            this.validationChanged();
        });
    } else if (this.locationLevel && this.locationLevel.toLocaleLowerCase() === "district") {
      this.loading = true;

      this.onChange = new Subject<string>();

      this.filteredFacilities = this.onChange
        .pipe(
          startWith(initialValue),
          debounceTime(100),
          distinctUntilChanged(),
          switchMap(
            (searchText) => {
              this.loading = true;
              const searchParms = new FacilitySearchParms();
              searchParms.Page = 1;
              searchParms.PageSize = 50;
              searchParms.DistrictSearch = searchText;
              searchParms.AreaSearch = searchText;
              searchParms.RegionSearch = searchText;
              const result = this.facilitySelectorService.getFacilities(this.selectorDataSource, 'district', searchParms);

              return result;
            }
          ),
          map(facilities => {
            const regions: string[] = _.orderBy(_.uniq(_.map(facilities, (facility) => { return facility.regionNumber })));
            const areas: string[] = _.orderBy(_.uniq(_.map(facilities, (facility) => { return facility.areaNumber })));

            const groupedLocations: LocationWithChildren[] = [];

            _.forEach(regions,
              (region) => {
                //var regionName = _.find(facilities, (facility) => { return facility.regionNumber === region; }).regionName;
                _.forEach(areas,
                  (area) => {
                    //var areaName = _.find(facilities, (facility) => { return facility.areaNumber === area; }).areaName;

                    const areaFacilities: Facility.Facility[] = _.filter(facilities,
                      (facility) => {
                        return facility.regionNumber === region &&
                          facility.areaNumber === area;
                      });

                    if (areaFacilities.length > 0) {
                      groupedLocations.push({
                        name: `${(region != null ? region + '/' : '')}${(area != null ? area : '')}`,
                        locations: _.orderBy(_.uniqWith(_.map(_.filter(facilities,
                          (facility) => {
                            return facility.regionNumber === region &&
                              facility.areaNumber === area;
                          }),
                          (facility) => {
                            return {
                              locationNumber: facility.districtNumber,
                              locationName: facility.districtName
                            } as SelectableFacility;
                          }), (arrVal, othVal) => {
                            return arrVal.locationNumber === othVal.locationNumber;
                          }),
                          (location) => {
                            return location.locationName;
                          })
                      } as LocationWithChildren);
                    }
                  });
              });

            this.loading = false;
            this.Initialize();

            return groupedLocations;
          })
        );

      this.selectedLocation = this.onChange
        .pipe(
          startWith(initialValue),
          debounceTime(100),
          distinctUntilChanged(),
          switchMap(
            (searchText) => {
              const searchParms = new FacilitySearchParms();
              searchParms.DistrictId = searchText;
              const result = this.facilitySelectorService.getFacilities(this.selectorDataSource, 'district', searchParms);

              return result.pipe(
                map(facilities => {
                  return _.find(_.uniqWith(_.map(facilities,
                    (facility) => {
                      return {
                        locationNumber: facility.districtNumber,
                        locationName: facility.districtName
                      } as SelectableFacility;
                    }), (arrVal, othVal) => {
                      return arrVal.locationNumber === othVal.locationNumber;
                    }),
                    (facility: SelectableFacility) => {
                      return facility.locationNumber === searchText;
                    }) as SelectableFacility;
                })
              );
            }
          )
        );

      this.selectedLocation
        .subscribe(result => {
          if (this.updateValue) {
            if (result)
              this.updateValue(result.locationNumber);
            else
              this.updateValue(null);
          }

          this.selectedFacility.emit(result);

          if (this.validationChanged)
            this.validationChanged();
        });

      this.onChange
        .subscribe((): void => {
          if (this.validationChanged)
            this.validationChanged();
        });
    }
    else if (this.locationLevel && this.locationLevel.toLocaleLowerCase() === "area") {
      this.loading = true;

      this.onChange = new Subject<string>();

      this.filteredFacilities = this.onChange
        .pipe(
          startWith(initialValue),
          debounceTime(100),
          distinctUntilChanged(),
          switchMap(
            (searchText) => {
              this.loading = true;

              const searchParms = new FacilitySearchParms();
              searchParms.Page = 1;
              searchParms.PageSize = 50;
              searchParms.AreaSearch = searchText;
              searchParms.RegionSearch = searchText;
              const result = this.facilitySelectorService.getFacilities(this.selectorDataSource, 'area', searchParms);

              return result;
            }
          ),
          map(facilities => {
            const regions: string[] = _.orderBy(_.uniq(_.map(facilities, (facility) => { return facility.regionNumber })));

            const groupedLocations: LocationWithChildren[] = [];

            _.forEach(regions,
              (region) => {
                //var regionName = _.find(facilities, (facility) => { return facility.regionNumber === region; }).regionName;
                const regionFacilities: Facility.Facility[] = _.filter(facilities,
                  (facility) => {
                    return facility.regionNumber === region;
                  });

                if (regionFacilities.length > 0) {
                  groupedLocations.push({
                    name: `${region}`,
                    locations: _.orderBy(_.uniqWith(_.map(_.filter(facilities,
                      (facility) => {
                        return facility.regionNumber === region;
                      }),
                      (facility) => {
                        return {
                          locationNumber: facility.areaNumber,
                          locationName: facility.areaName
                        } as SelectableFacility;
                      }), (arrVal, othVal) => {
                        return arrVal.locationNumber === othVal.locationNumber;
                      }),
                      (location: SelectableFacility) => {
                        return location.locationName;
                      })
                  } as LocationWithChildren);
                }
              });

            this.loading = false;
            this.Initialize();

            return groupedLocations;
          })
        );

      this.selectedLocation = this.onChange
        .pipe(
          startWith(initialValue),
          debounceTime(100),
          distinctUntilChanged(),
          switchMap(
            (searchText) => {
              const searchParms = new FacilitySearchParms();
              searchParms.AreaId = searchText;
              const result = this.facilitySelectorService.getFacilities(this.selectorDataSource, 'area', searchParms);

              return result.pipe(
                map(facilities => {
                  return _.find(_.uniqWith(_.map(facilities,
                    (facility) => {
                      return {
                        locationNumber: facility.areaNumber,
                        locationName: facility.areaName
                      } as SelectableFacility;
                    }), (arrVal, othVal) => {
                      return arrVal.locationNumber === othVal.locationNumber;
                    }),
                    (facility: SelectableFacility) => {
                      return facility.locationNumber === searchText;
                    }) as SelectableFacility;
                })
              );
            }
          )
        );

      this.selectedLocation
        .subscribe((result): void => {
          if (this.updateValue) {
            if (result)
              this.updateValue(result.locationNumber);
            else
              this.updateValue(null);
          }

          this.selectedFacility.emit(result);

          if (this.validationChanged)
            this.validationChanged();
        });

      this.onChange
        .subscribe((): void => {
          if (this.validationChanged)
            this.validationChanged();
        });
    }
    else if (this.locationLevel && this.locationLevel.toLocaleLowerCase() === "region") {
      this.loading = true;

      this.onChange = new Subject<string>();

      this.filteredFacilities = this.onChange
        .pipe(
          startWith(initialValue),
          debounceTime(100),
          distinctUntilChanged(),
          switchMap(
            (searchText) => {
              this.loading = true;

              const searchParms = new FacilitySearchParms();
              searchParms.Page = 1;
              searchParms.PageSize = 50;
              searchParms.RegionSearch = searchText;
              const result = this.facilitySelectorService.getFacilities(this.selectorDataSource, 'region', searchParms);

              return result;
            }
          ),
          map(facilities => {
            const groupedLocations: LocationWithChildren[] = [
              {
                name: 'Aegis Therapies',
                locations: _.orderBy(_.uniqWith(_.map(facilities,
                  (facility) => {
                    return {
                      locationNumber: facility.regionNumber,
                      locationName: facility.regionName
                    } as SelectableFacility;
                  }), (arrVal, othVal) => {
                    return arrVal.locationNumber === othVal.locationNumber;
                  }),
                  (location: SelectableFacility) => {
                    return location.locationName;
                  })
              } as LocationWithChildren
            ];

            this.loading = false;
            this.Initialize();

            return groupedLocations;
          })
        );

      this.selectedLocation = this.onChange
        .pipe(
          startWith(initialValue),
          debounceTime(100),
          distinctUntilChanged(),
          switchMap(
            (searchText) => {
              const searchParms = new FacilitySearchParms();
              searchParms.RegionId = searchText;
              const result = this.facilitySelectorService.getFacilities(this.selectorDataSource, 'region', searchParms);

              return result.pipe(
                map(facilities => {
                  return _.find(_.uniqWith(_.map(facilities,
                    (facility) => {
                      return {
                        locationNumber: facility.regionNumber,
                        locationName: facility.regionName
                      } as SelectableFacility;
                    }), (arrVal, othVal) => {
                      return arrVal.locationNumber === othVal.locationNumber;
                    }),
                    (facility: SelectableFacility) => {
                      return facility.locationNumber === searchText;
                    });
                })
              );
            }
          )
        );

      this.selectedLocation
        .subscribe((result): void => {
          if (this.updateValue) {
            if (result)
              this.updateValue(result.locationNumber);
            else
              this.updateValue(null);
          }

          this.selectedFacility.emit(result);

          if (this.validationChanged)
            this.validationChanged();
        });

      this.onChange
        .subscribe((): void => {
          if (this.validationChanged)
            this.validationChanged();
        });
    }
    else {
      this.onChange = null;
    }
  }

  public getPlaceholder(): string {
    if (!this.placeholder) {
      return `Select a ${this.getDisplayLevel()} ${this.getAddString()}`;
    }

    return this.placeholder;
  }

  public getDisplayLevel(): string {
    if (!this.locationLevel) {
      return "";
    } else {
      if (this.locationLevel.toLocaleLowerCase() === "facility")
        return "Facility";
      else if (this.locationLevel.toLocaleLowerCase() === "district")
        return "District";
      else if (this.locationLevel.toLocaleLowerCase() === "area")
        return "Area";
      else if (this.locationLevel.toLocaleLowerCase() === "region")
        return "Region";
      else
        return "";
    }
  }

  public getAddString(): string {
    return this.addString || "";
  }

  public openSearch(event: Event) {
    if (this.touchValue)
      this.touchValue();

    event.stopPropagation();

    const dialogRef: MatDialogRef<FacilitySelectorSearchDialog, any> = this.dialog.open(FacilitySelectorSearchDialog, {
      width: '400px',
      data: {
        searchType: this.locationLevel.toLocaleLowerCase(),
        selectorDataSource: this.selectorDataSource
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.searchText = result;
        if (this.onChange)
          this.onChange.next(this.searchText);
      }
    });
  }

  public ngOnInit() {
    this.initialize(this.searchText);
  }

  public writeValue(obj: any): void {
    //if (obj) {
      this.searchText = obj;
      if (this.onChange)
        this.onChange.next(this.searchText);

    //}
  }
  public registerOnChange(fn: any): void {
    this.updateValue = fn;

    if (this.onChange)
      this.initialize(this.searchText);
  }
  public registerOnTouched(fn: any): void {
    this.touchValue = fn;
  }
  public setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }
  public validate(c: AbstractControl): { [key: string]: any; } {
    return null;
  }
  public registerOnValidatorChange?(fn: () => void): void {
  }
}
